import { useState } from "react";
import { Brand } from "./Brand";
import { Button } from "./ui/Button";
import { Icon } from "./ui/Icon";

interface LoginPageProps {
  readonly error: string | null;
  readonly onEnterpriseLogin: () => void;
  readonly onPasswordLogin: (input: {
    readonly mode: "login" | "register";
    readonly email: string;
    readonly password: string;
    readonly name?: string;
  }) => Promise<void>;
}

/** Enterprise authentication gateway for password and SSO identities. */
export function LoginPage({ error, onEnterpriseLogin, onPasswordLogin }: LoginPageProps) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const activeError = error ?? localError;

  return (
    <div className="login-page">
      <aside className="login-aside">
        <Brand inverse />
        <div className="login-aside-content">
          <span className="login-kicker">SONOX DEVELOPER CLOUD</span>
          <h1>连接每一种应用，<br />承载每一次对话。</h1>
          <p>面向 App、Web 和服务端的一体化即时通信开放平台。</p>
          <div className="login-capabilities">
            <span><Icon name="activity" />全球实时网络</span>
            <span><Icon name="key" />租户级安全隔离</span>
            <span><Icon name="code" />多端 SDK 接入</span>
          </div>
        </div>
        <small>© 2026 SONOX · Enterprise IM Infrastructure</small>
      </aside>
      <main className="login-main">
        <section className="login-card">
          <div className="mobile-login-brand"><Brand /></div>
          <p className="eyebrow">DEVELOPER CONSOLE</p>
          <h2>{mode === "login" ? "欢迎回来" : "创建开发者账号"}</h2>
          <p className="login-lead">{mode === "login" ? "登录后管理应用、凭证和接入配置。" : "注册后即可创建第一个 IM 应用。"}</p>
          <div className="login-tabs" role="tablist" aria-label="账号方式">
            <button aria-selected={mode === "login"} className={mode === "login" ? "active" : ""} role="tab" type="button" onClick={() => { setMode("login"); setLocalError(null); }}>登录</button>
            <button aria-selected={mode === "register"} className={mode === "register" ? "active" : ""} role="tab" type="button" onClick={() => { setMode("register"); setLocalError(null); }}>注册</button>
          </div>
          <form className="login-form" onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const email = fieldValue(form, "email").trim();
            const password = fieldValue(form, "password");
            const name = fieldValue(form, "name").trim();
            setBusy(true);
            setLocalError(null);
            void onPasswordLogin({ mode, email, password, ...(mode === "register" ? { name } : {}) })
              .catch((cause: unknown) => { setLocalError(cause instanceof Error ? cause.message : String(cause)); })
              .finally(() => { setBusy(false); });
          }}>
            {mode === "register" ? <label className="form-field"><span>开发者名称</span><input name="name" autoComplete="name" required maxLength={64} placeholder="请输入名称" /></label> : null}
            <label className="form-field"><span>邮箱</span><input name="email" type="email" autoComplete="email" required maxLength={254} placeholder="name@example.com" /></label>
            <label className="form-field"><span>密码</span><input name="password" type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} required minLength={8} maxLength={128} placeholder="至少 8 个字符" /></label>
            {activeError === null ? null : <div className="form-error" role="alert">{activeError}</div>}
            <Button className="button-block" loading={busy} type="submit" variant="primary">{mode === "register" ? "注册并进入平台" : "登录"}</Button>
          </form>
          <div className="login-divider"><span>或使用企业身份</span></div>
          <Button className="button-block" leadingIcon="globe" type="button" onClick={onEnterpriseLogin}>企业 SSO 登录</Button>
          <p className="security-note"><Icon name="key" size={13} />Authorization Code + PKCE · Token 仅保存在当前会话</p>
        </section>
      </main>
    </div>
  );
}

function fieldValue(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value : "";
}
